/**
 * PURE. Colour maths for the Cennini three-tone ramps (art §2.3): sRGB hex ⇄ linear ⇄ OKLab, and `deriveRamp`.
 */
export type RGB = [number, number, number];
export type Lab = [number, number, number];

export function hexToSrgb(hex: string): RGB {
  const h = hex.replace('#', '');
  return [parseInt(h.slice(0, 2), 16) / 255, parseInt(h.slice(2, 4), 16) / 255, parseInt(h.slice(4, 6), 16) / 255];
}
export const srgbToHex = (c: RGB): string => '#' + c.map((x) => Math.round(Math.max(0, Math.min(1, x)) * 255).toString(16).padStart(2, '0')).join('').toUpperCase();
const toLin = (x: number): number => (x <= 0.04045 ? x / 12.92 : Math.pow((x + 0.055) / 1.055, 2.4));
const toSrgb = (x: number): number => (x <= 0.0031308 ? x * 12.92 : 1.055 * Math.pow(x, 1 / 2.4) - 0.055);
export const srgbToLinear = (c: RGB): RGB => [toLin(c[0]), toLin(c[1]), toLin(c[2])];
export const linearToSrgb = (c: RGB): RGB => [toSrgb(c[0]), toSrgb(c[1]), toSrgb(c[2])];

export function linearToOklab(c: RGB): Lab {
  const l = 0.4122214708 * c[0] + 0.5363325363 * c[1] + 0.0514459929 * c[2];
  const m = 0.2119034982 * c[0] + 0.6806995451 * c[1] + 0.1073969566 * c[2];
  const s = 0.0883024619 * c[0] + 0.2817188376 * c[1] + 0.6299787005 * c[2];
  const l_ = Math.cbrt(l), m_ = Math.cbrt(m), s_ = Math.cbrt(s);
  return [0.2104542553 * l_ + 0.793617785 * m_ - 0.0040720468 * s_, 1.9779984951 * l_ - 2.428592205 * m_ + 0.4505937099 * s_,
    0.0259040371 * l_ + 0.7827717662 * m_ - 0.808675766 * s_];
}
export function oklabToLinear(c: Lab): RGB {
  const l_ = c[0] + 0.3963377774 * c[1] + 0.2158037573 * c[2];
  const m_ = c[0] - 0.1055613458 * c[1] - 0.0638541728 * c[2];
  const s_ = c[0] - 0.0894841775 * c[1] - 1.291485548 * c[2];
  const l = l_ * l_ * l_, m = m_ * m_ * m_, s = s_ * s_ * s_;
  return [4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s, -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
    -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s];
}
export const hexToOklab = (hex: string): Lab => linearToOklab(srgbToLinear(hexToSrgb(hex)));
const clampRgb = (c: RGB): RGB => [Math.max(0, Math.min(1, c[0])), Math.max(0, Math.min(1, c[1])), Math.max(0, Math.min(1, c[2]))];

export interface Ramp { light: RGB; mid: RGB; shadow: RGB }
export interface ShadowTint { hex: string; k: number; dLmax?: number }

const LEAD_WHITE_LAB = hexToOklab('#F7F1E1');

/** Art §2.3 `deriveRamp` → LINEAR sRGB ramp. `override` replaces the scene tint (flesh: verdaccio, k 0.40). */
export function deriveRamp(midHex: string, scene: ShadowTint, override?: ShadowTint): Ramp {
  const mid = hexToOklab(midHex);
  const light: Lab = [mid[0] + (LEAD_WHITE_LAB[0] - mid[0]) * 0.28, mid[1] + (LEAD_WHITE_LAB[1] - mid[1]) * 0.28, mid[2] + (LEAD_WHITE_LAB[2] - mid[2]) * 0.28];
  const dL = Math.max(0.07, Math.min(scene.dLmax ?? 0.16, 0.26 * mid[0]));
  const t = override ?? scene;
  const tint = hexToOklab(t.hex);
  const k = override ? override.k : scene.k;
  const shadow: Lab = [mid[0] - dL, 0.95 * mid[1] + (tint[1] - 0.95 * mid[1]) * k, 0.95 * mid[2] + (tint[2] - 0.95 * mid[2]) * k];
  return { light: clampRgb(oklabToLinear(light)), mid: clampRgb(oklabToLinear(mid)), shadow: clampRgb(oklabToLinear(shadow)) };
}

/** A flat (unramped) colour: gold, eyes and UI never go through the ramp (art §2.3). */
export function flatRamp(hex: string): Ramp {
  const c = srgbToLinear(hexToSrgb(hex));
  return { light: c, mid: c, shadow: c };
}
